import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { OaktreeLogo } from './OaktreeLogo';
import { GlobalSearch } from './GlobalSearch';
import { sidebarAPI, candidateAPI, icfsAPI } from '../services/api';
import { useRealtimeEvents } from '../hooks/useRealtimeEvents';
import { 
  LayoutDashboard, Users, Briefcase, Building2, Calendar,
  BarChart3, Settings, LogOut, Menu, X, UserCog, ChevronRight,
  FileText, Laptop, Mail, ClipboardList, Shield,
  Sun, Moon, AlertTriangle, Inbox, Sparkles, RefreshCw, Trophy
} from 'lucide-react';
import { cn } from '../lib/utils';

// Navigation items with their tab names for permission checking and count keys
// `notifPage` (iter 96) maps to backend notification.page field for sidebar badges.
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, tab: 'dashboard', notifPage: 'dashboard' },
  // iter223 — LeadsGen role pages + team view for BDE/BDEM
  { name: 'Leads Gen', href: '/leadsgen', icon: Sparkles, tab: 'dashboard', roles: ['leadsgen'] },
  { name: 'Daily Activity', href: '/leadsgen/activity', icon: BarChart3, tab: 'dashboard', roles: ['leadsgen'] },
  { name: 'Leads Gen', href: '/team-leadsgen', icon: Sparkles, tab: 'dashboard', roles: ['bde', 'bde_manager'] },
  { name: 'Performance', href: '/performance', icon: Trophy, tab: 'dashboard', highlight: true, excludeRoles: ['bde', 'bde_manager', 'leadsgen'] },
  { name: 'AI Search', href: '/search', icon: Sparkles, tab: 'candidates', highlight: true, roles: ['admin', 'super_admin', 'bde_manager', 'bde', 'team_lead', 'recruiter', 'recruiter_manager'] },
  { name: 'Analysis', href: '/analysis', icon: BarChart3, tab: 'setup', roles: ['admin', 'super_admin'] },
  { name: 'Candidates', href: '/candidates', icon: Users, tab: 'candidates', countKey: 'candidates', notifPage: 'candidates' },
  // iter 211.1 — Inhouse Developers (B2C developer pool): Admin / BDEM / BDE only
  { name: 'Inhouse Developers', href: '/inhouse-developers', icon: UserCog, tab: 'candidates', roles: ['admin', 'super_admin', 'bde_manager', 'bde'] },
  { name: 'Gmail Sourcing', href: '/gmail-sourcing', icon: Inbox, tab: 'candidates', roles: ['admin', 'super_admin'] },
  { name: 'Gmail Sync Admin', href: '/gmail-sync-admin', icon: RefreshCw, tab: 'setup', roles: ['admin', 'super_admin'] },
  { name: 'Gamification Admin', href: '/gamification-admin', icon: Trophy, tab: 'setup', roles: ['admin', 'super_admin'] },
  { name: 'Jobs', href: '/jobs', icon: Briefcase, tab: 'jobs', countKey: 'jobs', notifPage: 'jobs' },
  // iter 140 — Clients hidden for Recruiter Manager (delivery role, not sales)
  { name: 'Clients', href: '/clients', icon: Building2, tab: 'clients', countKey: 'clients', notifPage: 'clients', excludeRoles: ['recruiter_manager'] },
  { name: 'Interviews', href: '/interviews', icon: Calendar, tab: 'interviews', countKey: 'interviews', excludeRoles: ['admin', 'super_admin', 'recruiter', 'team_lead', 'bde', 'bde_manager', 'recruiter_manager'] },
  // iter 140 — Pipeline visible to RM as well
  { name: 'Pipeline', href: '/pipeline', icon: Calendar, tab: 'interviews', countKey: 'candidates', roles: ['admin', 'super_admin', 'recruiter', 'team_lead', 'bde', 'bde_manager', 'recruiter_manager'], notifPage: 'pipeline' },
  // iter244 — ICF's page: shown only when the user has ≥1 eligible Turing submission (admin always)
  { name: "ICF's", href: '/icfs', icon: ClipboardList, tab: 'dashboard', roles: ['admin', 'super_admin', 'recruiter', 'team_lead', 'recruiter_manager', 'mis_turing'], requiresIcfs: true },
  { name: 'Workorders', href: '/workorders', icon: FileText, tab: 'workorders', countKey: 'workorders', notifPage: 'workorder', excludeRoles: ['recruiter_manager'] },
  { name: 'Timesheets', href: '/timesheets', icon: ClipboardList, tab: 'timesheets', countKey: 'timesheets', excludeRoles: ['recruiter', 'team_lead', 'admin', 'super_admin', 'recruiter_manager'] },
  { name: 'Laptops', href: '/laptops', icon: Laptop, tab: 'laptops', excludeRoles: ['recruiter_manager'] },
  { name: 'Email Requests', href: '/email-requests', icon: Mail, tab: 'email_requests', excludeRoles: ['admin', 'super_admin', 'recruiter_manager'] },
  { name: 'Analytics & Reports', href: '/analytics', icon: BarChart3, tab: 'analytics', excludeRoles: ['bde', 'bde_manager', 'team_lead', 'admin', 'super_admin', 'recruiter_manager'] },
  { name: 'Team', href: '/team', icon: UserCog, tab: 'team', excludeRoles: ['admin', 'super_admin'] },
  { name: 'Audit Logs', href: '/audit-logs', icon: Shield, tab: 'audit_logs', excludeRoles: ['recruiter_manager'] },
  { name: 'Setup', href: '/setup', icon: Settings, tab: 'setup', excludeRoles: ['recruiter_manager'] },
  { name: 'Settings', href: '/settings', icon: Settings, tab: 'settings', excludeRoles: ['leadsgen'] },
];

// iter 203 — MIS Turing role sees exactly two sidebar items inside RecruitPro
const misTuringNavigation = [
  { name: 'Dashboard', href: '/turing/dashboard', icon: LayoutDashboard, tab: 'dashboard' },
  { name: 'Turing Submissions', href: '/turing/submissions', icon: Briefcase, tab: 'dashboard' },
  { name: 'Micro Submissions', href: '/turing/micro', icon: Briefcase, tab: 'dashboard' },
  { name: "ICF's", href: '/icfs', icon: ClipboardList, tab: 'dashboard' },
];

// iter 205 — Internal Screening role sees exactly two sidebar items
const screeningNavigation = [
  { name: 'Dashboard', href: '/screening/dashboard', icon: LayoutDashboard, tab: 'dashboard' },
  { name: 'Internal Screening', href: '/screening/queue', icon: Users, tab: 'dashboard' },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasTabAccess, permissions, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [moduleCounts, setModuleCounts] = useState({});
  const [notifBadges, setNotifBadges] = useState({}); // iter 96: per-page unread notification counts

  // Fetch sidebar counts on mount and periodically
  const fetchCounts = useCallback(async () => {
    try {
      const response = await sidebarAPI.getCounts();
      setModuleCounts(response.data || {});
    } catch (error) {
      console.error('Failed to fetch sidebar counts:', error);
    }
  }, []);

  // iter 96: poll the unread notification summary every 5s and overlay
  // per-page badges on the sidebar (Pipeline, Workorder, etc.).
  const fetchNotifBadges = useCallback(async () => {
    try {
      const res = await candidateAPI.notificationsSummary();
      setNotifBadges(res.data?.by_page || {});
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchNotifBadges();
    // iter 185 — notif badge poll relaxed 5s → 30s (SSE covers real-time)
    const id = setInterval(fetchNotifBadges, 30000);
    return () => clearInterval(id);
  }, [user, fetchNotifBadges]);

  // iter244 — ICF's sidebar eligibility (recruiter/TL/RM only; admin always shown)
  const [icfsEligible, setIcfsEligible] = useState(false);
  const fetchIcfsEligibility = useCallback(async () => {
    if (!['recruiter', 'team_lead', 'recruiter_manager'].includes(user?.role)) return;
    try {
      const res = await icfsAPI.eligibility();
      setIcfsEligible(!!res.data?.eligible);
    } catch { /* keep last known value */ }
  }, [user?.role]);
  useEffect(() => { fetchIcfsEligibility(); }, [fetchIcfsEligibility]);

  useEffect(() => {
    if (user) {
      fetchCounts();
      // Refresh counts every 30 seconds
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchCounts]);

  // Refresh counts when location changes (new record might have been added)
  useEffect(() => {
    if (user) {
      fetchCounts();
    }
  }, [location.pathname, user, fetchCounts]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setLogoutDialogOpen(false);
  };

  // Filter navigation based on user's profile permissions
  let filteredNav = user?.role === 'mis_turing' ? misTuringNavigation : user?.role === 'internal_screening' ? screeningNavigation : navigation.filter(item => {
    // Check excludeRoles - if user's role is in excludeRoles, hide the item
    if (item.excludeRoles && user?.role) {
      if (item.excludeRoles.includes(user.role)) return false;
    }
    
    // Check role-based access if roles filter is specified — authoritative
    // gate, evaluated BEFORE the dashboard/settings shortcuts (iter223).
    if (item.roles && user?.role) {
      return item.roles.includes(user.role);
    }
    
    // Settings and Messages are always visible (unless excluded by role)
    if (item.tab === 'settings' || item.tab === 'messages') return true;
    
    // Dashboard is always visible
    if (item.tab === 'dashboard') return true;
    
    
    // Check tab visibility from user's profile
    return hasTabAccess(item.tab);
  });

  // iter239 — admin-granted MIS Turing Team page (server-enforced flag)
  if (user?.turing_team_access && !['admin', 'super_admin', 'mis'].includes(user?.role)) {
    filteredNav = [...filteredNav, { name: 'MIS Turing Team', href: '/turing-team-view', icon: Briefcase, tab: 'dashboard' }];
  }

  // iter244 — ICF's sidebar gate: admin always; recruiter/TL/RM only when they
  // have ≥1 eligible Job-Opening-workflow Turing submission (server-decided)
  filteredNav = filteredNav.filter((i) =>
    !i.requiresIcfs || ['admin', 'super_admin', 'mis_turing'].includes(user?.role) || icfsEligible);

  // iter239 — realtime sidebar permission propagation
  useRealtimeEvents({
    'access.updated': (evt) => {
      const d = evt?.data || {};
      if (!user?.id) return;
      if ((d.granted || []).includes(user.id) || (d.revoked || []).includes(user.id)) {
        refreshUser();
        if ((d.revoked || []).includes(user.id) && location.pathname === '/turing-team-view') {
          navigate('/dashboard');
        }
      }
    },
    // iter244 — first Turing submission makes ICF's appear without a refresh
    'turing.updated': () => fetchIcfsEligibility(),
    'candidate.created': () => fetchIcfsEligibility(),
  });

  return (
    <aside className={cn(
      "fixed left-0 top-0 h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 z-40 transition-all duration-300 flex flex-col",
      collapsed ? "w-20" : "w-64"
    )} data-testid="sidebar">
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100 bg-[#1E3A8A] flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2">
              <OaktreeLogo size={32} />
              <div className="flex flex-col leading-none">
                <span className="text-sm font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  OAKTREE
                </span>
                <span className="text-xs font-medium text-blue-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Recruit
                </span>
              </div>
            </Link>
            {/* iter 193 — Admin portal switcher (RecruitPro / MIS / B2C) */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <select
                className="ml-1 rounded-md bg-white/10 text-white text-xs px-1.5 py-1 border border-white/20 max-w-[92px]"
                value="recruitpro"
                onChange={(e) => {
                  if (e.target.value === 'mis') navigate('/mis');
                  if (e.target.value === 'b2c') navigate('/b2c');
                }}
                data-testid="portal-switcher"
              >
                <option value="recruitpro" className="text-slate-900">RecruitPro</option>
                <option value="mis" className="text-slate-900">MIS Portal</option>
                <option value="b2c" className="text-slate-900">B2C Portal</option>
              </select>
            )}
          </div>
        )}
        {collapsed && (
          <Link to="/dashboard" className="mx-auto">
            <OaktreeLogo size={32} />
          </Link>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="p-2 text-white hover:bg-[#2563EB]"
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-1 overflow-y-auto flex-1">
        {filteredNav.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const count = item.countKey ? moduleCounts[item.countKey] : null;
          const notifCount = item.notifPage ? (notifBadges[item.notifPage] || 0) : 0;

          // Mark module as viewed when clicked + clear notification badge for this page
          const handleModuleClick = async () => {
            if (item.countKey && count > 0) {
              try {
                await sidebarAPI.markViewed(item.countKey);
                fetchCounts();
              } catch (err) {
                console.error('Failed to mark module as viewed:', err);
              }
            }
            if (item.notifPage && notifCount > 0) {
              try {
                await candidateAPI.markNotificationPageRead(item.notifPage);
                fetchNotifBadges();
              } catch {
                /* silent */
              }
            }
          };
          
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleModuleClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                isActive
                  ? "bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium"
                  : item.highlight
                    ? "text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
                    : "text-[#1F2937] hover:bg-slate-50 hover:text-[#1E3A8A] dark:text-slate-300"
              )}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className={cn(
                "w-5 h-5", 
                collapsed && "mx-auto",
                item.highlight && !isActive && "text-amber-500"
              )} />
              {!collapsed && (
                <div className="flex items-center justify-between flex-1">
                  <span className="flex items-center gap-1.5">
                    {item.tab === 'candidates' && user?.role === 'hr' ? 'Shortlisted Candidates' : item.name}
                    {item.highlight && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700">
                        NEW
                      </Badge>
                    )}
                  </span>
                  {notifCount > 0 && (
                    <Badge
                      className="ml-1 text-[10px] px-1.5 py-0 min-w-[18px] h-4 bg-purple-500 text-white border-0"
                      data-testid={`nav-notif-${item.notifPage}`}
                      title={`${notifCount} new update${notifCount === 1 ? '' : 's'}`}
                    >
                      {notifCount > 99 ? '99+' : notifCount}
                    </Badge>
                  )}
                  {count !== null && count > 0 && (
                    <Badge 
                      variant={item.countKey === 'messages' ? 'destructive' : 'secondary'}
                      className={cn(
                        "ml-auto text-xs px-1.5 py-0.5 min-w-[20px] text-center",
                        item.countKey === 'messages' 
                          ? "bg-red-500 text-white" 
                          : "bg-blue-500 text-white"
                      )}
                    >
                      {count > 99 ? '99+' : count}
                    </Badge>
                  )}
                </div>
              )}
              {collapsed && (notifCount > 0 || (count !== null && count > 0)) && (
                <span className="absolute top-0 right-0 w-2 h-2 bg-purple-500 rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-100 bg-white dark:bg-slate-900 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1E3A8A] dark:bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1F2937] dark:text-slate-200 truncate">{user?.name}</p>
              <Badge variant="outline" className="text-xs capitalize text-[#2563EB] border-[#2563EB] dark:text-blue-400 dark:border-blue-400">
                {user?.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        )}
        
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          onClick={toggleTheme}
          className={cn(
            "w-full mb-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
            collapsed && "px-2"
          )}
          data-testid="theme-toggle-sidebar"
        >
          {theme === 'dark' ? (
            <Sun className={cn("w-5 h-5", !collapsed && "mr-2")} />
          ) : (
            <Moon className={cn("w-5 h-5", !collapsed && "mr-2")} />
          )}
          {!collapsed && (theme === 'dark' ? 'Light Mode' : 'Dark Mode')}
        </Button>
        
        <Button
          variant="ghost"
          onClick={() => setLogoutDialogOpen(true)}
          className={cn(
            "w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20",
            collapsed && "px-2"
          )}
          data-testid="logout-btn"
        >
          <LogOut className={cn("w-5 h-5", !collapsed && "mr-2")} />
          {!collapsed && "Logout"}
        </Button>
        
        {/* Logout Confirmation Dialog */}
        <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <DialogTitle>Confirm Logout</DialogTitle>
              </div>
              <DialogDescription>
                Are you sure you want to logout? You will need to login again to access the platform.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setLogoutDialogOpen(false)} data-testid="cancel-logout-btn">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleLogout} data-testid="confirm-logout-btn">
                Yes, Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </aside>
  );
};

export const Header = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/turing/micro')) return 'Micro Submissions';
    if (path.startsWith('/turing/submissions')) return 'Turing Submissions';
    if (path.startsWith('/turing/dashboard')) return 'Turing Dashboard';
    if (path.startsWith('/screening/queue')) return 'Internal Screening';
    if (path.startsWith('/screening/dashboard')) return 'Internal Screening Dashboard';
    if (path.startsWith('/candidates')) return user?.role === 'hr' ? 'Shortlisted Candidates' : 'Candidates';
    if (path.startsWith('/jobs')) return 'Job Openings';
    if (path.startsWith('/clients')) return 'Clients';
    if (path.startsWith('/interviews')) return 'Interviews';
    if (path.startsWith('/pipeline')) return 'Pipeline';
    if (path.startsWith('/workorders')) return 'Workorders';
    if (path.startsWith('/timesheets')) return 'Timesheets';
    if (path.startsWith('/laptops')) return 'Laptops';
    if (path.startsWith('/email-requests')) return 'Email Requests';
    if (path.startsWith('/messages')) return 'Messages';
    if (path.startsWith('/analytics')) return 'Analytics';
    if (path.startsWith('/team')) return 'Team Management';
    if (path.startsWith('/settings')) return 'Settings';
    return 'OAKTREE Recruit';
  };

  return (
    <header className="h-16 bg-[#1E3A8A] dark:bg-slate-900 border-b border-[#1E3A8A] dark:border-slate-700 flex items-center justify-between px-6 lg:hidden">
      <Button variant="ghost" size="sm" onClick={onMenuClick} className="text-white hover:bg-[#2563EB]">
        <Menu className="w-6 h-6" />
      </Button>
      <h1 className="font-semibold text-white">{getPageTitle()}</h1>
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleTheme} 
          className="text-white hover:bg-[#2563EB] p-2"
          data-testid="theme-toggle-mobile"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-white">
            {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </span>
        </div>
      </div>
    </header>
  );
};

export default function Layout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <Sidebar collapsed={false} onToggle={() => setMobileMenuOpen(false)} />
        </div>
      )}

      {/* Mobile Header */}
      <Header onMenuClick={() => setMobileMenuOpen(true)} />

      {/* Main Content */}
      <main className={cn(
        "transition-all duration-300 min-h-screen",
        sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"
      )}>
        {/* Desktop Top Bar with Global Search */}
        <div className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <GlobalSearch />
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>Press</span>
            <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600 font-mono text-xs">⌘K</kbd>
            <span>to search</span>
          </div>
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
